import type { PaletteInstance } from '../art/types';
import { bindKeys } from '../input/InputController';
import { RaceRenderer } from '../render/RaceRenderer';
import { CommentaryTicker, PHOTO_MARGIN_S, verdictFor } from '../sim/race/commentary';
import { RaceSim } from '../sim/race/race-sim';
import type { RaceResult, RaceSetup } from '../sim/race/types';
import { RaceHud } from '../ui/RaceHud';
import { showResults } from '../ui/ResultsPanel';
import type { Scene, SceneContext } from './SceneManager';

export interface RaceSceneOptions {
  /** Career mode: called after the scene pops. Also disables reruns —
   * a career race happens once. */
  onDone?: (result: RaceResult) => void;
  backLabel?: string;
  /** The featured horse's coat for lane 0. */
  playerPalette?: PaletteInstance;
}

/** One race day: countdown, spacebar, the wire, the results card. */
export class RaceScene implements Scene {
  private readonly setup: RaceSetup;
  private readonly options: RaceSceneOptions;
  private ctx!: SceneContext;
  private sim!: RaceSim;
  private renderer!: RaceRenderer;
  private hud!: RaceHud;
  private unbind: (() => void) | null = null;
  private disposeResults: (() => void) | null = null;
  private rerunCount = 0;
  private ticker!: CommentaryTicker;
  private history: { tick: number; xs: number[] }[] = [];
  private photo: { index: number; endIndex: number } | null = null;
  private verdict: string | undefined;

  constructor(setup: RaceSetup, options: RaceSceneOptions = {}) {
    this.setup = setup;
    this.options = options;
  }

  enter(ctx: SceneContext): void {
    this.ctx = ctx;
    this.sim = new RaceSim(this.setup);
    this.ticker = new CommentaryTicker(
      [this.setup.playerName, ...this.setup.opponents.map((o) => o.name)],
      this.setup.seed,
    );
    this.renderer = new RaceRenderer(ctx.scaler.root, this.setup, this.options.playerPalette);
    this.hud = new RaceHud(ctx.uiRoot, this.setup.opponents.length + 1);
    this.unbind = bindKeys([
      {
        code: 'Space',
        handler: () => {
          if (!this.sim.done) this.sim.enqueue({ kind: 'tap' });
        },
      },
      {
        code: 'KeyR',
        inModal: true,
        handler: () => {
          if (this.sim.done && !this.options.onDone) this.again();
        },
      },
      {
        code: 'KeyT',
        inModal: true,
        handler: () => {
          if (this.sim.done) this.back();
        },
      },
      {
        code: 'Enter',
        inModal: true,
        handler: () => {
          if (this.sim.done) this.back();
        },
      },
      {
        code: 'Escape',
        inModal: true,
        handler: () => {
          if (this.sim.done) this.back();
          else this.sim.enqueue({ kind: 'abandon' });
        },
      },
    ]);
  }

  frame(nowMs: number): void {
    // The photo replay owns the frame until it plays out.
    if (this.photo) {
      this.photo.index = Math.min(this.photo.index + 0.25, this.photo.endIndex);
      const snapshot = this.history[Math.floor(this.photo.index)];
      if (snapshot) this.renderer.drawRaw(snapshot.xs);
      if (this.photo.index >= this.photo.endIndex) {
        this.photo = null;
        this.openResults();
      }
      return;
    }

    const frame = this.sim.advance(nowMs);
    const events = this.sim.drainEvents();
    this.hud.handleEvents(events);
    const line = this.ticker.consume(events, this.sim.state.tick);
    if (line) this.hud.setCommentary(line);
    const lastTick = this.history[this.history.length - 1]?.tick;
    if (lastTick !== this.sim.state.tick) {
      this.history.push({ tick: this.sim.state.tick, xs: this.sim.state.lanes.map((l) => l.x) });
      if (this.history.length > 240) this.history.shift();
    }
    this.renderer.draw(this.sim.state, frame.alpha);
    this.hud.update(this.sim);
    this.ctx.fps?.frame(nowMs, frame.steps, frame.droppedSeconds);

    if (this.sim.done && !this.disposeResults && !this.photo) {
      const result = this.sim.result()!;
      const first = result.placings[0]?.timeS;
      const second = result.placings[1]?.timeS;
      const margin = first != null && second != null ? second - first : Number.POSITIVE_INFINITY;
      if (!result.abandoned && margin < PHOTO_MARGIN_S && this.history.length > 30) {
        this.verdict = `won by ${verdictFor(margin)}`;
        this.hud.setCommentary("It's a photo! They're checking the print…");
        const endIndex = this.history.length - 1;
        this.photo = { index: Math.max(0, endIndex - 90), endIndex };
        return;
      }
      this.openResults();
    }
  }

  private openResults(): void {
    if (this.disposeResults) return;
    const result = this.sim.result()!;
    this.disposeResults = showResults(this.ctx.uiRoot, result, {
      onAgain: this.options.onDone ? undefined : () => this.again(),
      onBack: () => this.back(),
      backLabel: this.options.backLabel,
      verdict: this.verdict,
    });
  }

  /** Same field, fresh seed — AI jitter and starts reshuffle. */
  private again(): void {
    this.disposeResults?.();
    this.disposeResults = null;
    this.history = [];
    this.photo = null;
    this.verdict = undefined;
    this.ticker = new CommentaryTicker(
      [this.setup.playerName, ...this.setup.opponents.map((o) => o.name)],
      this.setup.seed + this.rerunCount + 1,
    );
    this.rerunCount += 1;
    this.sim = new RaceSim({ ...this.setup, seed: (this.setup.seed + this.rerunCount) >>> 0 });
    this.hud.reset();
  }

  private back(): void {
    const result = this.sim.result();
    this.ctx.scenes.pop();
    if (result && this.options.onDone) this.options.onDone(result);
  }

  exit(): void {
    this.unbind?.();
    this.disposeResults?.();
    this.disposeResults = null;
    this.hud.dispose();
    this.renderer.destroy();
  }
}
